/**
 * The report builder, as the Configure panel's second view.
 *
 * Opened by "Edit report" on a report built on the dashboard (or a number
 * tile), and by "Build a new report". It is the SAME builder the Reports page
 * uses — every control a report has, with a live preview — so a dashboard
 * report is never built with less than a custom report is.
 *
 * DONE CHANGES THE PANEL'S DRAFT, NOT THE DASHBOARD. It hands the spec back to
 * the settings view, which the author still Applies; Back throws the builder's
 * changes away.
 *
 * THE PERIOD IS THE DASHBOARD'S. The builder shows one so the preview has
 * something to run over; the dashboard's is what gets stored.
 */
import {
  describeSpec,
  ReportBuilder,
  type ReportBuilderHandle,
  type ReportDraft,
} from '@clockwise/report-kit';
import type { ReportModel, ReportSpec } from '@clockwise/semantic-core';
import { canVisualize } from '@clockwise/semantic-core';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { reportFilterControl } from '@/components/reports/report-filter-control';
import { Button } from '@/components/ui/button';
import { analyticsApi } from '@/lib/api';
import { tileSeriesStyle } from './tile-palette';

/**
 * Why the draft cannot be used as a NUMBER tile, or `null` when it can.
 *
 * Asked of the same guard the builder uses to offer "Metric", so the button
 * and the builder cannot disagree about what a metric can show.
 */
export function metricRefusal(draft: ReportSpec | null, model?: ReportModel): string | null {
  // NOT RUNNABLE, for whatever reason — no measure, a filter chip with no
  // value yet, a bad period. The builder's own preview names which.
  if (!draft) return 'Finish the setup — the preview says what is missing.';
  if (draft.kind !== 'pivot') return 'A number tile shows a summary, not a listing.';
  // Unchecked is not the same as fine: a model this viewer cannot read has no
  // catalog entry to check a metric against.
  if (!model) return 'This tile’s data source is not available to you.';
  const verdict = canVisualize(model, draft, 'metric');
  return verdict.ok ? null : verdict.reason;
}

/**
 * The name a report built on the dashboard is placed under when none was
 * typed: the draft described in words — "Client Billable Hours by Pay Type",
 * exactly what the builder's heading shows an unnamed report as.
 */
export function describedName(spec: ReportSpec | null, model?: ReportModel): string {
  return spec && model ? describeSpec(model, spec) : '';
}

interface Props {
  /** A number tile pins the draft to one number. */
  metric: boolean;
  models: readonly ReportModel[];
  /** Where the builder starts; absent for a new report. */
  initialSpec?: ReportSpec;
  onDone: (spec: ReportSpec) => void;
  onBack: () => void;
  /** Reports whether the builder holds changes that Done has not handed back. */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * The tile's colours, so the preview is painted the way the placed tile
   * will be rather than in the Reports page's palette.
   */
  palette?: readonly string[];
}

export function BuilderPanel({
  metric,
  models,
  initialSpec,
  onDone,
  onBack,
  onDirtyChange,
  palette,
}: Props) {
  const transport = useMemo(
    () => ({
      runQuery: (s: ReportSpec, signal?: AbortSignal) => analyticsApi.query(s, signal),
    }),
    [],
  );
  const builder = useRef<ReportBuilderHandle>(null);
  const [draft, setDraft] = useState<ReportSpec | null>(initialSpec ?? null);
  // EDITED once the builder emits after its FIRST draft: it re-emits only when
  // its draft changed, and comparing with `initialSpec` would call an untouched
  // report edited, since the builder normalises what it is handed. A report
  // started from nothing settles over more than one emission, so there it only
  // counts once there is something to lose — a report that can run.
  const emissions = useRef(0);

  // THE BUILDER'S BAND IS THIS BODY. Its heights are fractions of
  // `--rk-viewport` — the page band on the Reports page — so it is set to the
  // body's measured height rather than a guess at the header and footer,
  // which wrap. The body is `flex-1` in a fixed-height sheet, so its height
  // does not depend on what the builder inside it does: no feedback loop.
  const body = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = body.current;
    if (!el) return;
    const sync = () => el.style.setProperty('--rk-viewport', `${el.clientHeight}px`);
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  });

  const model = models.find((m) => m.id === draft?.model);
  const refusal = metric
    ? metricRefusal(draft, model)
    : draft
      ? null
      : 'Finish the setup — the preview says what is missing.';

  const done = () => {
    const next = builder.current?.draft().spec ?? null;
    if (!next) return;
    if (metric) {
      if (
        metricRefusal(
          next,
          models.find((m) => m.id === next.model),
        )
      )
        return;
      onDone({ ...next, viz: 'metric', stacked: false });
      return;
    }
    onDone(next);
  };

  return (
    <>
      <div
        ref={body}
        className="report-kit-scope min-h-0 flex-1 overflow-auto px-6 py-4"
        // Asked of the DRAFT, so it follows the Columns shelf as it is edited:
        // a dimension moved there takes the tile back to the named palette.
        style={tileSeriesStyle(draft ?? initialSpec, palette)}
      >
        <ReportBuilder
          models={models as ReportModel[]}
          transport={transport}
          initialSpec={initialSpec}
          // The tile's title lives in the settings view — one place to type it.
          editable={false}
          showTitle={false}
          renderFilterControl={reportFilterControl}
          onDraftChange={(next: ReportDraft) => {
            emissions.current += 1;
            const started = initialSpec !== undefined || next.spec !== null;
            if (emissions.current > 1 && started) onDirtyChange?.(true);
            setDraft(next.spec);
          }}
          handle={builder}
        />
      </div>
      <div className="flex items-center justify-end gap-2 border-t px-6 py-3">
        {refusal && <p className="mr-auto text-muted-foreground text-xs">{refusal}</p>}
        <Button type="button" variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button type="button" onClick={done} disabled={refusal !== null}>
          Done
        </Button>
      </div>
    </>
  );
}
